"use client";

import { Button } from "@cmp-src/primitives/button";
import { Modal } from "@gettactic/components/src/overlay/modals/Modal";
import { Copy } from "lucide-react";
import React, { useCallback } from "react";
import toast from "react-hot-toast";
import type { RegisteredGuest } from "./PostReservationVisitorPrompt";

interface RegistrationSummaryModalProps {
  registeredGuests: RegisteredGuest[];
  onClose: () => void;
}

export const RegistrationSummaryModal: React.FC<
  RegistrationSummaryModalProps
> = ({ registeredGuests, onClose }) => {
  const guestsWithCodes = registeredGuests.filter((g) => g.code);

  const handleCopyAll = useCallback(async () => {
    const text = guestsWithCodes.map((g) => `${g.email}: ${g.code}`).join("\n");
    await navigator.clipboard.writeText(text);
    toast.success("Codes copied to clipboard");
  }, [guestsWithCodes]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Visitors Pre-Registered"
      description="Emails have been sent to the visitors with their pre-registration codes. For reference, these are the codes visitors will use upon arriving at the office."
      size="sm"
    >
      <div className="flex flex-col gap-3 py-2">
        {guestsWithCodes.map((guest) => (
          <div
            key={guest.email}
            className="flex items-center justify-between rounded-lg bg-neutral-100 px-4 py-3"
          >
            <span className="truncate text-sm">{guest.email}</span>
            <span className="ml-3 shrink-0 font-bold font-mono tracking-widest">
              {guest.code}
            </span>
          </div>
        ))}
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button variant="outline" className="gap-2" onClick={handleCopyAll}>
            <Copy className="h-4 w-4" />
            Copy All Codes
          </Button>
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
};
